import { Slot } from 'radix-ui';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cx } from '~/lib/cx';

import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** A 16px icon before the label. Stays decorative: the label carries the meaning. */
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  /**
   * Render the child element instead of a `<button>`, keeping the styling.
   *
   * The reason this exists is accessibility rather than convenience: a link
   * styled as a button must still be an `<a>`, because a `<button>` that
   * navigates breaks middle-click, Ctrl-click and "copy link address", and a
   * `<div role="button">` breaks keyboard activation in ways nobody notices
   * until an audit.
   */
  asChild?: boolean;
  /**
   * Show the control as working.
   *
   * Sets `aria-busy` and disables activation *without* setting `disabled`:
   * a disabled button loses focus, and losing focus mid-action throws a
   * keyboard user back to the top of the document. The pointer is blocked in
   * CSS and the click is swallowed in the handler instead.
   */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    iconStart,
    iconEnd,
    asChild = false,
    loading = false,
    className,
    children,
    onClick,
    type,
    ...rest
  },
  ref,
) {
  const Component = asChild ? Slot.Root : 'button';

  return (
    <Component
      ref={ref}
      // An unspecified `type` inside a form is `submit`, which is how a
      // "Cancel" button ends up submitting the form it sits in.
      {...(asChild ? {} : { type: type ?? 'button' })}
      className={cx(styles.button, styles[variant], styles[size], className)}
      aria-busy={loading || undefined}
      data-loading={loading || undefined}
      onClick={
        loading
          ? (event) => {
              event.preventDefault();
            }
          : onClick
      }
      {...rest}
    >
      {iconStart ? <span className={styles.icon}>{iconStart}</span> : null}
      {/* `Slottable` marks which child is the element `asChild` should become,
       * so a button with an icon either side can still render as an <a>.
       * Without it Radix sees three children and refuses — correctly: it
       * cannot guess whether the icon or the label is the anchor. Outside a
       * Slot it is transparent, so the ordinary path is unaffected. */}
      <Slot.Slottable>
        <span className={styles.label}>{children}</span>
      </Slot.Slottable>
      {iconEnd ? <span className={styles.icon}>{iconEnd}</span> : null}
    </Component>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'iconStart' | 'iconEnd' | 'children'> {
  /**
   * Required, and the whole point of the component.
   *
   * An icon-only control has no accessible name unless one is given, and the
   * type signature is the only thing that reliably prevents shipping one
   * without. It becomes both the `aria-label` and the tooltip, so the two
   * cannot disagree.
   */
  label: string;
  icon: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, variant = 'ghost', size = 'md', className, ...rest },
  ref,
) {
  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      aria-label={label}
      title={label}
      className={cx(styles.iconButton, className)}
      {...rest}
    >
      {icon}
    </Button>
  );
});
